/** Transport decoding and inert navigation only. Authentication and receipt
 * verification remain with the existing channel handlers and consumers. */
export const MIRROR_APP_HANDOFF_URL = "https://www.aivyv.com/mirror" as const;
export type MirrorAppHandoffReason = "app_review" | "app_only" | "native_required";
export interface MirrorAppHandoff {
  type: "app_handoff";
  reason: MirrorAppHandoffReason;
  url: typeof MIRROR_APP_HANDOFF_URL;
  executed: false;
  receipt: null;
}
export function createMirrorAppHandoff(reason: MirrorAppHandoffReason): MirrorAppHandoff {
  return { type: "app_handoff", reason, url: MIRROR_APP_HANDOFF_URL, executed: false, receipt: null };
}
export type MirrorWhatsAppToolDisposition = "silent_checkin" | "app_handoff" | "deny";
/** The caller supplies the actual assembled catalog. No transport action is
 * executable here; the existing literal-signal gate owns silent check-ins. */
export function classifyWhatsAppTool(
  name: string, catalogToolNames: ReadonlySet<string>,
): MirrorWhatsAppToolDisposition {
  if (!catalogToolNames.has(name)) return "deny";
  return name === "record_checkin" ? "silent_checkin" : "app_handoff";
}

export interface MirrorTurnEnvelope {
  response: string;
  proposals: Record<string, unknown>[];
  action_results: Record<string, unknown>[];
  session_id: string;
  message_id?: string;
  credit_balance_units?: number | null;
  handoff?: MirrorAppHandoff;
}
export interface MirrorQuotaMetadata {
  kind: "text" | "stt" | "tts" | "live";
  scope: "daily" | "monthly";
  used: number;
  limit: number;
  period_start: string;
  period_end?: string;
  current_tier: "free" | "premium" | "pro" | "ultra";
  upgrade_tier?: "free" | "premium" | "pro" | "ultra" | null;
}
export type MirrorChannelOutcome =
  | { kind: "final"; httpStatus: number; envelope: MirrorTurnEnvelope }
  | { kind: "handoff"; httpStatus: 403; code: string; handoff: MirrorAppHandoff }
  | { kind: "quota"; httpStatus: 402; code: "quota_exceeded"; quota: MirrorQuotaMetadata }
  | { kind: "locked"; httpStatus: 402; code: "module_locked"; module: string }
  | { kind: "denied"; httpStatus: number; code: string }
  | { kind: "failed"; httpStatus: number; code: string };

const APP_ONLY_ERRORS = new Set([
  "file_edit_app_only", "windsor_report_app_only", "creator_app_only",
  "connected_message_app_only", "connected_task_app_only", "connected_file_app_only",
]);
export const MIRROR_CHANNEL_MAX_BYTES = 1_048_576;
const MAX_RESPONSE_CHARS = 262_144;
const MAX_RESULTS = 100;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const tier = (value: unknown): value is MirrorQuotaMetadata["current_tier"] =>
  value === "free" || value === "premium" || value === "pro" || value === "ultra";
const timestamp = (value: unknown): value is string =>
  typeof value === "string" && value.length <= 80 && Number.isFinite(Date.parse(value));
const codeOf = (value: unknown, fallback: string): string =>
  typeof value === "string" && /^[a-z][a-z0-9_]{0,119}$/.test(value) ? value : fallback;
function handoffOf(value: unknown): MirrorAppHandoff | null {
  if (!record(value) || Object.keys(value).length !== 5 ||
    value.type !== "app_handoff" || !["app_review", "app_only", "native_required"].includes(String(value.reason)) ||
    value.url !== MIRROR_APP_HANDOFF_URL || value.executed !== false || value.receipt !== null) return null;
  return createMirrorAppHandoff(value.reason as MirrorAppHandoffReason);
}
function envelopeOf(value: unknown, expectedSessionId: string): MirrorTurnEnvelope | null {
  if (!record(value) || typeof value.response !== "string" || value.response.length > MAX_RESPONSE_CHARS ||
    typeof value.session_id !== "string" || !UUID.test(value.session_id) ||
    value.session_id.toLowerCase() !== expectedSessionId.toLowerCase() ||
    !Array.isArray(value.proposals) || value.proposals.length > MAX_RESULTS || !value.proposals.every(record) ||
    !Array.isArray(value.action_results) || value.action_results.length > MAX_RESULTS || !value.action_results.every(record)) return null;
  if (value.message_id !== undefined && (typeof value.message_id !== "string" || !UUID.test(value.message_id))) return null;
  if (value.credit_balance_units !== undefined && value.credit_balance_units !== null &&
    (typeof value.credit_balance_units !== "number" || !Number.isFinite(value.credit_balance_units) || value.credit_balance_units < 0)) return null;
  const handoff = value.handoff === undefined ? undefined : handoffOf(value.handoff);
  if (handoff === null || handoff && (value.proposals.length !== 0 || value.action_results.length !== 0)) return null;
  if (!value.response.trim() && value.proposals.length === 0 && value.action_results.length === 0) return null;
  return {
    response: value.response, proposals: value.proposals, action_results: value.action_results,
    session_id: value.session_id,
    ...(value.message_id !== undefined ? { message_id: value.message_id as string } : {}),
    ...(value.credit_balance_units !== undefined ? { credit_balance_units: value.credit_balance_units as number | null } : {}),
    ...(handoff ? { handoff } : {}),
  };
}
function quotaOf(value: unknown): MirrorQuotaMetadata | null {
  if (!record(value) || !["text", "stt", "tts", "live"].includes(String(value.kind)) ||
    !["daily", "monthly"].includes(String(value.scope)) ||
    typeof value.used !== "number" || !Number.isFinite(value.used) || value.used < 0 ||
    typeof value.limit !== "number" || !Number.isFinite(value.limit) || value.limit < 0 ||
    !timestamp(value.period_start) || !tier(value.current_tier) ||
    value.period_end !== undefined && !timestamp(value.period_end) ||
    value.upgrade_tier !== undefined && value.upgrade_tier !== null && !tier(value.upgrade_tier)) return null;
  return {
    kind: value.kind as MirrorQuotaMetadata["kind"], scope: value.scope as MirrorQuotaMetadata["scope"],
    used: value.used, limit: value.limit, period_start: value.period_start, current_tier: value.current_tier,
    ...(value.period_end !== undefined ? { period_end: value.period_end as string } : {}),
    ...(value.upgrade_tier !== undefined ? { upgrade_tier: value.upgrade_tier as MirrorQuotaMetadata["upgrade_tier"] } : {}),
  };
}
async function readBounded(response: Response): Promise<string> {
  const length = Number(response.headers.get("content-length"));
  if (Number.isFinite(length) && length > MIRROR_CHANNEL_MAX_BYTES) throw new Error("response_too_large");
  if (!response.body) return "";
  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8", { fatal: true });
  let text = "", bytes = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      bytes += value.byteLength;
      if (bytes > MIRROR_CHANNEL_MAX_BYTES) throw new Error("response_too_large");
      text += decoder.decode(value, { stream: true });
    }
    return text + decoder.decode();
  } catch (error) {
    try { await reader.cancel(); } catch { /* The underlying stream may already be closed. */ }
    throw error;
  } finally {
    reader.releaseLock();
  }
}
function streamPayload(text: string): { payload?: unknown; error?: string } {
  let event = "", data: string[] = [], payload: unknown, finalCount = 0, error: string | undefined;
  const dispatch = () => {
    if (!data.length) { event = ""; return; }
    if (event === "final" || event === "error") {
      let parsed: unknown;
      try { parsed = JSON.parse(data.join("\n")); } catch { error = "invalid_stream_payload"; }
      if (event === "error") error = record(parsed) ? codeOf(parsed.error, "stream_failed") : "stream_failed";
      else {
        finalCount++;
        if (finalCount !== 1) error = "ambiguous_final";
        payload = parsed;
      }
    }
    event = ""; data = [];
  };
  // A terminal frame requires the SSE blank line. EOF never authenticates a
  // partially delivered JSON final as a completed response.
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const remainder = lines.pop() || "";
  for (const line of lines) {
    if (line === "") dispatch();
    else if (line.startsWith("event:")) event = line.slice(6).replace(/^ /, "").trim();
    else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
  }
  if (data.length || remainder) return { error: "truncated_stream" };
  if (error) return { error };
  return finalCount === 1 ? { payload } : { error: "missing_final" };
}
/** Decodes one existing server turn. It never retries, authenticates a user,
 * executes proposals or upgrades transported action results into receipts. */
export async function readMirrorChannelOutcome(
  response: Response, options: { expectedSessionId: string },
): Promise<MirrorChannelOutcome> {
  const httpStatus = response.status;
  if (!UUID.test(options.expectedSessionId)) return { kind: "failed", httpStatus, code: "invalid_session" };
  let text: string;
  try { text = await readBounded(response); } catch (error) {
    return { kind: "failed", httpStatus, code: error instanceof Error && error.message === "response_too_large" ? "response_too_large" : "response_unavailable" };
  }
  let value: unknown;
  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  if (contentType.includes("text/event-stream") && response.ok) {
    const streamed = streamPayload(text);
    if (streamed.error) return { kind: "failed", httpStatus, code: streamed.error };
    value = streamed.payload;
  } else {
    try { value = JSON.parse(text); } catch {
      if (httpStatus === 401 || httpStatus === 403) return { kind: "denied", httpStatus, code: "channel_denied" };
      return { kind: "failed", httpStatus, code: "invalid_response" };
    }
  }
  if (!response.ok) {
    const code = record(value) ? codeOf(value.error, "request_failed") : "request_failed";
    if (httpStatus === 403 && APP_ONLY_ERRORS.has(code)) return {
      kind: "handoff", httpStatus: 403, code, handoff: createMirrorAppHandoff("app_only"),
    };
    if (httpStatus === 401 || httpStatus === 403) return { kind: "denied", httpStatus, code };
    if (httpStatus === 402 && code === "quota_exceeded") {
      const quota = quotaOf(value);
      return quota ? { kind: "quota", httpStatus: 402, code, quota } : { kind: "failed", httpStatus, code: "invalid_quota_response" };
    }
    if (httpStatus === 402 && code === "module_locked" && record(value) &&
      typeof value.module === "string" && /^[a-z][a-z0-9_]{0,79}$/.test(value.module)) {
      return { kind: "locked", httpStatus: 402, code, module: value.module };
    }
    return { kind: "failed", httpStatus, code };
  }
  const envelope = envelopeOf(value, options.expectedSessionId);
  return envelope ? { kind: "final", httpStatus, envelope } : { kind: "failed", httpStatus, code: "invalid_turn" };
}
